import { Location } from '@angular/common';
import { Component, OnInit, Input, OnDestroy, inject, output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, AbstractControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ReplaySubject, forkJoin } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { CognitoService } from 'app/services/cognito.service';
import { ModalErrorComponent, ModalErrorData } from 'app/shared/modal-error/modal-error.component';
import { PhoneFormatPipe } from 'app/shared/pipes/phone-format.pipe';

import { CognitoCodes, getErrorMessage } from '../../CognitoCodes';

interface ForgotErrors {
  password?: string;
  confirmationCode?: string;
  phoneNumber?: string;
}
@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
  providers: [PhoneFormatPipe],
  standalone: false,
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
  private cognitoService = inject(CognitoService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private phoneFormatPipe = inject(PhoneFormatPipe);
  private location = inject(Location);
  private toasterService = inject(ToastrService);

  // Handles old auth input
  public code = new UntypedFormControl(null, {
    validators: [Validators.required, Validators.max(999999), Validators.min(100000)],
  });
  // Password handling
  public password = new UntypedFormControl(null, { validators: Validators.minLength(6) });
  public confirmPassword = new UntypedFormControl();

  // Handle email input
  public forgotLoginInput = '';
  public flagPhoneEmailInput = '';

  // International
  public forgotSelectedCountryCode = '+1';
  public forgotValidNumber = false;
  public forgotSelectedCountry = 'US';

  public phoneForgotForm = new UntypedFormGroup({ phoneForgot: new UntypedFormControl() });
  public phoneForgotFormLastValuePhoneNumber?: string;

  public errors: ForgotErrors = {};
  public showVerifyingText = false;
  public codeSent = false;
  public returnURL?: string;

  // Context for onIntegration
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() public onIntegration = null;
  public readonly signUp = output<boolean>();

  private destroyed$ = new ReplaySubject<boolean>(1);

  public ngOnInit(): void {
    this.phoneForgotForm.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.errors = {};
    });
    this.password.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => this.validatePassword());
    this.confirmPassword.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => this.validatePassword());
    this.code.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => (this.errors = {}));
    this.route.queryParams
      .pipe(filter((params) => params.return))
      .pipe(takeUntil(this.destroyed$))
      .subscribe((params) => {
        this.returnURL = params.return;
      });

    this.detectChanges();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  // Handles Event from flag component
  public requestCodeEvent(input: string) {
    this.flagPhoneEmailInput = input;
  }

  private detectChanges(): void {
    // Transform Phone Value to better format
    const forgotPhoneControl = this.getForgotPhone;
    forgotPhoneControl?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      if (forgotPhoneControl.value && forgotPhoneControl.value.number !== this.phoneForgotFormLastValuePhoneNumber) {
        this.phoneForgotFormLastValuePhoneNumber = forgotPhoneControl.value.number;
        const formattedPhone = this.phoneFormatPipe.transform(
          forgotPhoneControl.value.number,
          forgotPhoneControl.value.countryCode,
        );
        forgotPhoneControl.setValue(formattedPhone, { onlySelf: true, emitEvent: false });
      }
    });
  }

  public validatePassword(): void {
    const password = this.password.value;
    const newPassword = this.confirmPassword.value;
    if (this.password.hasError('minlength') || this.confirmPassword.hasError('minlength')) {
      this.errors = { password: 'Password must be at least 6 characters' };
    } else {
      if (password && newPassword && password !== '' && newPassword !== '') {
        if (password === newPassword) {
          this.errors = {};
        } else {
          this.errors = { password: 'Your passwords do not match' };
        }
      } else {
        this.errors = {};
      }
    }
  }

  /**
    Requests a verification code for a forgotten login input.
    @param {string} [forgotLoginInput] - The forgotten login input (email) to send the verification code to. If not provided, the value will be retrieved from the getForgotInputValue method.
    @returns {void}
    */
  public requestCode(forgotLoginInput?: string): void {
    this.showVerifyingText = true;
    this.forgotLoginInput = forgotLoginInput || this.getForgotInputValue();

    if (this.forgotLoginInput != null) {
      this.cognitoService
        .forgotPassword(this.forgotLoginInput)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            this.showVerifyingText = false;
            this.codeSent = true;
            this.errors = {};
            if (forgotLoginInput) {
              this.toasterService.success('A verification code has been sent to your email address.', 'Success');
            }
          },
          error: (err: unknown) => {
            this.showVerifyingText = false;
            this.codeSent = false;
            const error = err as { code: CognitoCodes };
            this.errors = { phoneNumber: getErrorMessage(error.code) };
          },
        });
    } else {
      this.showVerifyingText = false;
      this.errors = { phoneNumber: 'THIS IS NOT A VALID EMAIL' };
      return;
    }
  }

  private getForgotInputValue(): string {
    return this.flagPhoneEmailInput;
  }

  public submitNewPassword(): void {
    const { code, confirmPassword, forgotLoginInput, password } = this;
    if (
      forgotLoginInput != null &&
      password.value != null &&
      code.value != null &&
      password.value === confirmPassword.value
    ) {
      const subscribe = this.cognitoService.forgotPasswordSubmit(
        forgotLoginInput,
        this.code.value.toString(),
        this.password.value,
      );
      const signInSubscribe = this.cognitoService.signIn(forgotLoginInput, this.password.value);
      forkJoin([subscribe, signInSubscribe])
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            if (this.onIntegration != null) {
              this.signUp.emit(true);
            } else {
              void this.router.navigate(['home']);
            }
          },
          error: (err: unknown) => {
            const error = err as { code: CognitoCodes };
            this.errors = { confirmationCode: getErrorMessage(error.code) };
          },
        });
    }
  }

  public requestCodeFormInvalid(): boolean {
    if (this.flagPhoneEmailInput || this.codeSent) {
      return false;
    }

    return true;
  }

  public openDialog(message: string): void {
    const dialogRef = this.dialog.open<ModalErrorComponent, ModalErrorData>(ModalErrorComponent, { data: message });

    dialogRef.afterClosed().pipe(takeUntil(this.destroyed$)).subscribe();
  }

  // handlers for International Sign In / Sign Up
  public forgotHasError(status: boolean): void {
    if (status) {
      this.forgotValidNumber = status;
    } else {
      this.errors = { phoneNumber: 'THIS IS NOT A VALID EMAIL OR PHONE NUMBER' };
      return;
    }
  }

  public handleBack(): void {
    if (this.codeSent) {
      this.codeSent = false;
    } else {
      this.location.back();
    }
  }

  public get getForgotPhone(): AbstractControl | null {
    return this.phoneForgotForm.get('phoneForgot');
  }
}
